import * as React from 'react';

import {Button} from 'shells/button/button';
import {SaveButton} from 'shells/button/save-button/save-button';
import {FormField} from 'shells/form/form-field';
import {Modal} from 'shells/modal/modal';
import {TextField} from 'shells/textfield/textfield';

import {GraphQLSelectAudiences} from '../../core/select/graphql-select-audiences';
import {GraphQLSelectAudiencesProvider} from '../../core/select/graphql-select-audiences-provider';

import {useMakeAudiencePublic, useGetPublicAudience} from './graphql/hooks';

type Props = {
    audienceId: string | null | undefined;
    onClose: () => void;
    isOpen: boolean;
};

export function PublicAudienceModal(props: Props) {
    const [audienceId, setAudienceId] = React.useState<string | null | undefined>(
        props.audienceId ? props.audienceId : null
    );

    const {audience: selectedAudience, isLoading: isLoadingAudience} =
        useGetPublicAudience(audienceId);

    const [publicName, setPublicName] = React.useState<string | null | undefined>(null);
    const [publicDescription, setPublicDescription] = React.useState<string | null | undefined>(
        null
    );

    React.useEffect(() => {
        if (selectedAudience) {
            setPublicName(selectedAudience.publicName || selectedAudience.name);
            setPublicDescription(selectedAudience.publicDescription);
        }
    }, [selectedAudience]);

    const {makeAudiencePublic, isLoading: isSaving} = useMakeAudiencePublic();

    React.useEffect(() => {
        if (!props.audienceId && props.isOpen) {
            const timer = setTimeout(() => {
                document.getElementById('audience-select-input')?.focus();
            }, 500);

            return () => clearTimeout(timer);
        }
    }, [props.isOpen, props.audienceId]);

    return (
        <Modal
            isOpen={props.isOpen}
            onClose={props.onClose}
            headerText={props.audienceId ? 'Edit audience' : 'Add audience to preference center'}
            headerSubtext={
                props.audienceId
                    ? 'Edit the public-facing information for this audience.'
                    : 'Select an audience and add public-facing information for your recipients.'
            }
            closeButton={false}
            fitHeight={true}
            maxWidth={450}
            visibleOverflow={true}
            trapFocus={false}
        >
            <div className='flex flex-dir-col'>
                <FormField innerId='audienceId' label='Audience' isRequired={true}>
                    <GraphQLSelectAudiencesProvider
                        disablePublicAudiences={true}
                        excludeWebFXAudiences={true}
                    >
                        {({options, isLoading}) => {
                            return (
                                <GraphQLSelectAudiences
                                    options={options}
                                    isLoading={isLoading}
                                    isCreateable={true}
                                    selectedId={audienceId}
                                    onChange={(audience) => {
                                        setAudienceId(audience.value);
                                    }}
                                    // @ts-expect-error
                                    menuPlacement='bottom'
                                    isDisabled={Boolean(props.audienceId)}
                                    inputId='audience-select-input'
                                    autoFocus={false}
                                    menuPortalTarget={document.body}
                                />
                            );
                        }}
                    </GraphQLSelectAudiencesProvider>
                </FormField>
                {audienceId && !isLoadingAudience && (
                    <>
                        <FormField innerId='name' label='Public name' isRequired={true}>
                            <TextField
                                id='name'
                                // @ts-expect-error
                                name='name'
                                size='normal'
                                type='text'
                                value={publicName || ''}
                                onChange={(value) => setPublicName(value)}
                                ignorePasswordAutoFill={true}
                                autoFocus={true}
                            />
                        </FormField>
                        <FormField
                            innerId='description'
                            label='Public description'
                            isRequired={true}
                        >
                            <TextField
                                id='description'
                                // @ts-expect-error
                                name='description'
                                size='normal'
                                type='text'
                                value={publicDescription || ''}
                                onChange={(value) => setPublicDescription(value)}
                                ignorePasswordAutoFill={true}
                            />
                        </FormField>
                    </>
                )}
                <div className='flex justify-sb pb-32'>
                    <Button variant='text-secondary' noPadding={true} onClick={props.onClose}>
                        Cancel
                    </Button>
                    <SaveButton
                        // @ts-expect-error
                        onClick={() => {
                            if (audienceId) {
                                makeAudiencePublic({
                                    audienceId,
                                    publicName,
                                    publicDescription,
                                    isPublic: true,
                                });
                            }
                        }}
                        isSaving={isSaving}
                        saveCompleteTimeout={1000}
                        onClearSaveComplete={() => {
                            props.onClose();
                        }}
                        disabled={
                            !audienceId ||
                            !isInputValid(publicName) ||
                            !isInputValid(publicDescription)
                        }
                    />
                </div>
            </div>
        </Modal>
    );
}

const isInputValid = (value?: string | null): boolean => {
    if (!value) return false;

    // Check if the input contains at least one non-whitespace character
    return /\S/.test(value);
};
